import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to always send credentials (cookies)
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      // Cookie is sent automatically due to withCredentials
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      // Not authenticated - cookie invalid or missing
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    
    // Check if 2FA is required
    if (response.data.requires_2fa) {
      return { requires_2fa: true, email: response.data.email, message: response.data.message };
    }
    
    // Check if approval is required
    if (response.data.requires_approval) {
      return { requires_approval: true, message: response.data.message };
    }
    
    // Cookie is set by server - just update user state
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const verify2FA = async (email, passcode) => {
    const response = await axios.post(`${API}/auth/verify-passcode`, { email, passcode });
    // Cookie is set by server
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, phone, role = 'homeowner', turnstileToken = null) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, phone, role, turnstile_token: turnstileToken });
    // Cookie is set by server
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleCallback = async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId });
    // Cookie is set by server
    const { user: userData } = response.data;
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    try {
      // Server will clear the httpOnly cookie
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Continue with local logout even if API fails
    }
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const value = {
    user,
    loading,
    login,
    verify2FA,
    register,
    logout,
    loginWithGoogle,
    handleGoogleCallback,
    updateUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
